import React, { useCallback, useEffect, useMemo, useState } from 'react'
import FacilityCostsSummary from './costs/FacilityCostsSummary'
import { useLocation, useNavigate } from 'react-router-dom'
import PageShell from '../components/PageShell'
import Drawer from '../components/Drawer'
import FormField from '../components/FormField'
import DateInput from '../components/DateInput'
import DateTimeLocalInput from '../components/DateTimeLocalInput'
import { getLocalizedDataset, getLocalizedDatasets, localizeReportRows } from '../utils/reportLabels'
import { columnKind, COLUMN_KIND_ICON, defaultReportColumns, orderedReportColumns, REPORT_DATASET_GROUPS } from '../utils/reportColumns'
import { loadStockCountDifferences } from '../utils/reportDatasets'
import ReportColumnPicker from './reports/ReportColumnPicker'
import ReportPager, { DEFAULT_REPORT_PAGE_SIZE } from './reports/ReportPager'
import StockViewExplainer, { StockViewSummary, summarizeStockDifferences, type StockDifferenceSummary } from './reports/StockViewExplainer'
import { ensureFarmList } from '../utils/farmWizard'
import { lotApi } from '../utils/lotApi'
import { applyReportScope, sortRowsNewestFirst, type ReportScopeV1 } from '../utils/reportScope'
import { deletedLotRecordsLeftOut, prepareReportLotFilter } from '../utils/reportDeletedLots'
import { cellTextAlign, decorateRowsWithRemovedFarmLotLabel, isNumericLikeValue, stringifyCell } from '../utils/reportCellDisplay'
import { downloadTextBlob, escapeCsvCell } from './datatable/datatableHelpers'
import { useT } from '../i18n'
import {
  createReport,
  deleteReport,
  loadReports,
  seedDefaultReportsIfEmpty,
  seedLotGrowthPerformanceReportIfMissing,
  seedCoreMonitoringReportIfMissing,
  upsertReport,
  type ReportFxConfigV1,
  type ReportDatasetKey,
  type ReportDefinitionV1,
  type ReportVisibility
} from '../utils/reportStorage'
import {
  getReportSnapshotArtifactDetailBySourceReportId,
  publishReportSnapshotArtifact
} from '../utils/reportArtifactStorage'
import type { WizardFarm, WizardProductionUnit } from '../types/farmWizard'
import type { Lot } from '../types/lot'

type RunState = {
  reportId: string
  loading: boolean
  error: string | null
  rows: Array<Record<string, unknown>>
  /** Findings #206: deleted lots whose records this run left out. */
  deletedLotRecords?: number
  /** Findings #222: what "Stock reconciliado" spread; absent on a recorded report. */
  stockDifferences?: StockDifferenceSummary | null
}

const DRAFT_PREVIEW_ROWS = 20

// A failed lookup leaves the report's rows as they are.
const summarizeScopeDifferences = async (scope: ReportScopeV1 | null | undefined): Promise<StockDifferenceSummary | null> => {
  if (scope?.stockView !== 'reconciled') return null
  return summarizeStockDifferences(await loadStockCountDifferences(scope).catch(() => []))
}

const emptyScope = (): ReportScopeV1 => ({
  fromDate: '',
  toDate: '',
  farmIds: [],
  productionUnitIds: [],
  lotIds: [],
  lotKeys: []
})

const emptyFx = (): ReportFxConfigV1 => ({
  targetCurrency: '',
  asOf: new Date().toISOString()
})

const normalizeFxDraft = (value: ReportFxConfigV1 | null): ReportFxConfigV1 | null => {
  if (!value) return null
  return {
    targetCurrency: value.targetCurrency.trim().toUpperCase(),
    asOf: value.asOf.trim()
  }
}

const isFxDraftValid = (value: ReportFxConfigV1 | null): boolean => {
  if (!value) return true
  const targetCurrency = value.targetCurrency.trim().toUpperCase()
  if (!/^[A-Z]{3}$/.test(targetCurrency)) return false
  const parsedAsOf = new Date(value.asOf.trim()).getTime()
  return Number.isFinite(parsedAsOf)
}

const isVisibility = (value: string): value is ReportVisibility => value === 'private' || value === 'team'

// Shared look for the report-results tables (both the saved-report results
// table and the drawer's draft-preview table) — plain unstyled <table>
// markup otherwise falls back to raw browser defaults.
const tableWrapperStyle: React.CSSProperties = {
  overflow: 'auto',
  marginTop: 12,
  border: '1px solid #e5e7eb',
  borderRadius: 12
}

// Results can run into the hundreds of rows (event-level datasets) — a
// bounded, independently-scrollable table with its own sticky header
// keeps the row count from just growing the whole page taller, and keeps
// the header in view instead of scrolling off with row 1.
const scrollableTableWrapperStyle: React.CSSProperties = {
  ...tableWrapperStyle,
  maxHeight: 480
}

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse'
}

export const tableHeaderCellStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '10px 16px',
  fontSize: 12,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
  color: '#475569',
  backgroundColor: '#f8fafc',
  borderBottom: '1px solid #e5e7eb',
  borderRight: '1px solid #f1f5f9',
  whiteSpace: 'nowrap',
  position: 'sticky',
  top: 0,
  zIndex: 1
}

const sortableTableHeaderCellStyle: React.CSSProperties = {
  ...tableHeaderCellStyle,
  cursor: 'pointer',
  userSelect: 'none'
}

export const tableCellStyle = (isLastRow: boolean): React.CSSProperties => ({
  padding: '10px 16px',
  verticalAlign: 'top',
  borderBottom: isLastRow ? 'none' : '1px solid #f1f5f9',
  borderRight: '1px solid #f1f5f9'
})

export const tableRowStyle = (rowIndex: number): React.CSSProperties => ({
  backgroundColor: rowIndex % 2 === 0 ? '#ffffff' : '#f8fafc'
})

const buildCsvContent = (columns: Array<{ key: string; label: string }>, rows: Array<Record<string, unknown>>, unserializableLabel: string): string => {
  const header = columns.map((column) => escapeCsvCell(column.label)).join(',')
  const lines = rows.map((row) => (
    columns.map((column) => escapeCsvCell(stringifyCell(row[column.key], unserializableLabel))).join(',')
  ))
  return [header, ...lines].join('\r\n')
}

const sanitizeFileName = (name: string): string => {
  const cleaned = name.trim().replace(/[^a-zA-Z0-9-_ ]/g, '').trim()
  return cleaned.length > 0 ? cleaned : 'report'
}

const ChoiceList = (props: {
  title: string
  clearLabel: string
  emptyLabel: string
  choices: Array<{ key?: string; value: string; label: string }>
  selectedValues: string[]
  onChange: React.Dispatch<React.SetStateAction<string[]>>
}): React.ReactNode => {
  const { title, clearLabel, emptyLabel, choices, selectedValues, onChange } = props
  const selected = new Set(selectedValues)

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'baseline' }}>
        <div style={{ fontWeight: 700 }}>{title}</div>
        <button
          type="button"
          onClick={() => onChange([])}
          disabled={selectedValues.length === 0}
        >
          {clearLabel}
        </button>
      </div>

      {choices.length === 0 ? (
        <div className="lead" style={{ marginTop: 6 }}>{emptyLabel}</div>
      ) : (
        <div
          style={{
            marginTop: 8,
            border: '1px solid #e5e7eb',
            borderRadius: 12,
            padding: 10,
            maxHeight: 220,
            overflow: 'auto',
            WebkitOverflowScrolling: 'touch'
          }}
        >
          <div style={{ display: 'grid', gap: 8 }}>
            {choices.map((choice) => {
              const checked = selected.has(choice.value)
              return (
                <label key={choice.key ?? `${choice.value}::${choice.label}`} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(event) => {
                      onChange((prev) => {
                        const next = new Set(prev)
                        if (event.target.checked) {
                          next.add(choice.value)
                        } else {
                          next.delete(choice.value)
                        }
                        return Array.from(next)
                      })
                    }}
                  />
                  <span>{choice.label}</span>
                </label>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

function DeletedLotRecordsNote({ count }: { count: number }): React.ReactElement | null {
  const t = useT()
  if (count <= 0) return null
  return (
    <p className="lead" data-testid="reports-deleted-lot-records" style={{ fontSize: 13, color: '#475569' }}>
      {count === 1 ? t('reports.deletedLot.leftOutOne') : t('reports.deletedLot.leftOut', { count })}
    </p>
  )
}

export default function Reports() {
  const t = useT()
  const location = useLocation()
  const navigate = useNavigate()
  const [reports, setReports] = useState<ReportDefinitionV1[]>(() => loadReports())
  const [activeReportId, setActiveReportId] = useState<string>(() => reports[0]?.id ?? '')
  const [runState, setRunState] = useState<RunState>(() => ({
    reportId: '',
    loading: false,
    error: null,
    rows: []
  }))
  const [sortState, setSortState] = useState<{ columnKey: string; direction: 'asc' | 'desc' } | null>(null)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(DEFAULT_REPORT_PAGE_SIZE)

  const [farms, setFarms] = useState<WizardFarm[]>([])
  const [lots, setLots] = useState<Lot[]>([])

  // A row's farm may no longer exist in `farms` (removed after the row's
  // history was recorded) — decorateRowsWithRemovedFarmLotLabel uses this
  // to flag those rows instead of letting them look like a current lot.
  const knownFarmIds = useMemo(() => new Set(farms.map((farm) => farm.id)), [farms])

  const [newName, setNewName] = useState('')
  const [newVisibility, setNewVisibility] = useState<ReportVisibility>('private')
  // Findings #221: the drawer starts with no report type chosen.
  const [newDatasetKey, setNewDatasetKey] = useState<ReportDatasetKey | ''>('')
  const [newColumns, setNewColumns] = useState<string[]>([])

  const [newScope, setNewScope] = useState<ReportScopeV1>(() => emptyScope())
  const [newFx, setNewFx] = useState<ReportFxConfigV1 | null>(null)
  // v2.2 T3-B-08: when non-empty, the Save button upserts in-place
  // against this id (preserving createdAt) instead of minting a new
  // report. Populated by the Saved-reports list's Edit handler; cleared
  // on Save, Cancel, or when the target report disappears from the
  // reports list (e.g. deleted in another tab).
  const [editingReportId, setEditingReportId] = useState<string>('')
  const [editingCreatedAt, setEditingCreatedAt] = useState<string>('')
  const [editingOriginalName, setEditingOriginalName] = useState<string>('')
  const [publishError, setPublishError] = useState<string | null>(null)
  const [publishStatus, setPublishStatus] = useState<string | null>(null)
  const [publishedArtifactVersion, setPublishedArtifactVersion] = useState<{
    version: number
    publishedAt: string | null
  } | null>(null)

  // Shape-up: the create/edit form is a drawer trigger, not permanent
  // on-screen real estate, mirroring Panel's widget-form drawer.
  const [isCreateFormOpen, setIsCreateFormOpen] = useState(false)
  const [draftPreview, setDraftPreview] = useState<{
    loading: boolean
    error: string | null
    rows: Array<Record<string, unknown>>
    deletedLotRecords?: number
  } | null>(null)
  const [draftDifferences, setDraftDifferences] = useState<StockDifferenceSummary | null>(null)

  const clearLabel = t('reports.choice.clear')
  const emptyChoiceLabel = t('reports.choice.noneAvailable')
  const requestedReportId = useMemo(() => {
    const params = new URLSearchParams(location.search)
    return params.get('reportId')?.trim() ?? ''
  }, [location.search])

  const isNewDatasetFxCapable = newDatasetKey === 'lotCostPerKg'
  const fxDraftValid = isFxDraftValid(newFx)
  const fxDraftEnabled = isNewDatasetFxCapable && newFx !== null

  const activeReport = useMemo(() => (
    reports.find((report) => report.id === activeReportId) ?? null
  ), [activeReportId, reports])

  const activeDataset = useMemo(() => (
    activeReport ? getLocalizedDataset(t, activeReport.datasetKey) : null
  ), [activeReport])

  useEffect(() => {
    if (!activeReport) {
      setPublishedArtifactVersion(null)
      setPublishError(null)
      setPublishStatus(null)
      return
    }

    const detail = getReportSnapshotArtifactDetailBySourceReportId(activeReport.id)
    setPublishedArtifactVersion(detail
      ? {
          version: detail.overview.currentVersion,
          publishedAt: detail.overview.publishedAt
        }
      : null)
    setPublishError(null)
    setPublishStatus(null)
  }, [activeReport?.id])

  // v2.2 T3-B-05d, split 2026-09-12: seed the harvest + in-flight
  // cost-per-kg reports on first tenant load, or migrate a tenant that
  // already has the pre-split single report (rename it + add the
  // in-flight companion). `seedDefaultReportsIfEmpty` is idempotent and
  // never overwrites an operator's edit — see its own docstring. Uses a
  // useEffect (not the useState initializer) so the seed/migration is
  // observable in tests and the current translated names are used.
  // Unconditional `setReports` (not just on the was-empty case) because
  // the migration path can also change an already-non-empty list.
  useEffect(() => {
    seedDefaultReportsIfEmpty(
      {
        harvest: t('reports.seed.costPerKgHarvest.name'),
        inFlight: t('reports.seed.costPerKgInFlight.name')
      },
      t('reports.seed.costPerKg.name')
    )
    // PRD `docs/reports-panel-fcr-sgr-sfr-timeseries-prd.md` item (c):
    // same one-shot bootstrap for the lotGrowthPerformance report, so it's
    // present whether the operator visits Reportes or Panel first (Panel
    // seeds it too — see NewDashboard.tsx). Reads the persisted list back
    // fresh, so it already reflects seedDefaultReportsIfEmpty's writes
    // above.
    seedLotGrowthPerformanceReportIfMissing(
      t('reports.seed.lotGrowthPerformance.name')
    )
    // v2.2 findings.md #45: core-monitoring report (mortality rate /
    // stock / average weight), seeded from Panel too — same reasoning.
    const withCoreMonitoring = seedCoreMonitoringReportIfMissing(
      t('reports.seed.coreMonitoring.name')
    )
    setReports(withCoreMonitoring)
    // Intentionally run once on mount — seeding is a one-shot bootstrap
    // and does not need to re-run when the translation key changes at
    // runtime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (reports.length === 0) {
      if (activeReportId) {
        setActiveReportId('')
      }
      return
    }

    if (requestedReportId) {
      const requestedExists = reports.some((report) => report.id === requestedReportId)
      if (requestedExists) {
        if (requestedReportId !== activeReportId) {
          setActiveReportId(requestedReportId)
        }
        return
      }
    }

    const activeExists = reports.some((report) => report.id === activeReportId)
    if (!activeExists) {
      setActiveReportId(reports[0]?.id ?? '')
    }
  }, [reports, requestedReportId])

  // v2.2 T3-B-08: defensive cleanup — if the report being edited
  // disappears from the reports list (e.g. deleted in another tab), clear
  // the edit state so Save doesn't inadvertently recreate the removed
  // report at the persisted id.
  useEffect(() => {
    if (!editingReportId) return
    const stillExists = reports.some((report) => report.id === editingReportId)
    if (!stillExists) {
      setEditingReportId('')
      setEditingCreatedAt('')
      setEditingOriginalName('')
    }
  }, [reports, editingReportId])

  useEffect(() => {
    let cancelled = false

    const loadChoices = async () => {
      try {
        const [nextFarms, nextLots] = await Promise.all([
          ensureFarmList().catch(() => []),
          lotApi.list().catch(() => [])
        ])

        if (cancelled) return
        setFarms(nextFarms)
        setLots(nextLots)
      } catch {
        // ignore
      }
    }

    void loadChoices()
    return () => {
      cancelled = true
    }
  }, [])

  // Findings #206: load the deleted lots before a run so every dataset leaves
  // their records out (Biomasa / Panel do the same).
  const prepareDeletedLotFilter = async () => {
    const [farmList, lotList] = await Promise.all([
      farms.length > 0 ? farms : ensureFarmList().catch(() => [] as WizardFarm[]),
      lots.length > 0 ? lots : lotApi.list().catch(() => [] as Lot[])
    ])
    await prepareReportLotFilter({
      farmIds: farmList.map((farm) => String(farm.id ?? '')).filter(Boolean),
      lots: lotList
    }).catch(() => undefined)
  }

  const handleRun = async (report: ReportDefinitionV1) => {
    const dataset = getLocalizedDataset(t, report.datasetKey)
    if (!dataset) {
      setRunState({ reportId: report.id, loading: false, error: t('reports.error.unknownDataset', { datasetKey: report.datasetKey }), rows: [] })
      return
    }

    setActiveReportId(report.id)
    setRunState({ reportId: report.id, loading: true, error: null, rows: [] })

    try {
      // v2.2 T3-B-06c: pass the report's saved scope into fetchRows so
      // aggregate datasets (currently only `lotCostPerKg`) can filter
      // their input event streams by `recordedAt` before rolling them
      // up. Event-level datasets ignore the scope arg; the shared
      // `applyReportScope` post-processor below still handles date +
      // facility filtering off the emitted rows' own fields.
      await prepareDeletedLotFilter()
      const [rows, stockDifferences] = await Promise.all([
        dataset.fetchRows(report.scope, report),
        summarizeScopeDifferences(report.scope)
      ])
      setRunState({ reportId: report.id, loading: false, error: null, rows, deletedLotRecords: deletedLotRecordsLeftOut(), stockDifferences })
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      setRunState({ reportId: report.id, loading: false, error: message, rows: [] })
    }
  }

  const handleDelete = (id: string) => {
    const next = deleteReport(id)
    setReports(next)

    if (activeReportId === id) {
      setActiveReportId(next[0]?.id ?? '')
      setRunState({ reportId: '', loading: false, error: null, rows: [] })
    }

    // v2.2 T3-B-08: if the operator deletes the report currently being
    // edited (either from this list item or via another tab reflected
    // through storage), clear the edit state so Save doesn't recreate
    // the just-deleted report.
    if (editingReportId === id) {
      setEditingReportId('')
      setEditingCreatedAt('')
      setEditingOriginalName('')
    }
  }

  // v2.2 T3-B-08: load a saved report into the create form for in-place
  // editing. Save handler below detects `editingReportId` and reuses the
  // id + createdAt so `upsertReport` overwrites the existing entry
  // instead of minting a new one.
  const handleStartEdit = (report: ReportDefinitionV1) => {
    setEditingReportId(report.id)
    setEditingCreatedAt(report.createdAt)
    setEditingOriginalName(report.name)
    setNewName(report.name)
    setNewDatasetKey(report.datasetKey)
    setNewVisibility(report.visibility)
    setNewColumns(report.columns.slice())
    setNewScope(report.scope ? { ...emptyScope(), ...report.scope } : emptyScope())
    setNewFx(report.datasetKey === 'lotCostPerKg' && report.fx ? { ...report.fx } : null)
    setDraftPreview(null)
    setIsCreateFormOpen(true)
  }

  const handleCancelEdit = () => {
    setEditingReportId('')
    setEditingCreatedAt('')
    setEditingOriginalName('')
    setNewName('')
    setNewScope(emptyScope())
    setNewFx(null)
    setDraftPreview(null)
    setIsCreateFormOpen(false)
  }

  // Trigger-card entry point (mirrors Panel's Add widget button): opens the
  // drawer with a blank draft, clearing any stale edit-in-progress state.
  const handleOpenCreateNew = () => {
    setEditingReportId('')
    setEditingCreatedAt('')
    setEditingOriginalName('')
    setNewName('')
    setNewDatasetKey('')
    setNewColumns([])
    setNewScope(emptyScope())
    setNewFx(null)
    setDraftPreview(null)
    setIsCreateFormOpen(true)
  }

  // Drawer's close (X / overlay click / Cancel) — routes through the
  // existing edit-cancel path when editing so state stays in one place.
  const handleCloseCreateForm = () => {
    if (editingReportId.trim().length > 0) {
      handleCancelEdit()
      return
    }
    setNewName('')
    setNewScope(emptyScope())
    setNewFx(null)
    setDraftPreview(null)
    setIsCreateFormOpen(false)
  }

  // Seeds the drawer from an existing report as a new (unsaved) draft —
  // same fields as handleStartEdit, but without editingReportId so Save
  // mints a new report instead of overwriting this one.
  const handleDuplicate = (report: ReportDefinitionV1) => {
    setEditingReportId('')
    setEditingCreatedAt('')
    setEditingOriginalName('')
    setNewName(t('reports.duplicate.namePrefix', { name: report.name }))
    setNewDatasetKey(report.datasetKey)
    setNewVisibility(report.visibility)
    setNewColumns(report.columns.slice())
    setNewScope(report.scope ? { ...emptyScope(), ...report.scope } : emptyScope())
    setNewFx(report.datasetKey === 'lotCostPerKg' && report.fx ? { ...report.fx } : null)
    setDraftPreview(null)
    setIsCreateFormOpen(true)
  }

  // Panel widgets are built on top of a saved report (picked from a
  // dropdown of existing reports) — this skips that manual re-find-it
  // step by deep-linking Panel straight into its Add Widget drawer with
  // this report pre-selected.
  const handleSendToPanel = (report: ReportDefinitionV1) => {
    navigate(`/dashboard-new?reportId=${encodeURIComponent(report.id)}&openWidgetForm=1`)
  }

  const datasetForNew = useMemo(() => (newDatasetKey ? getLocalizedDataset(t, newDatasetKey) : undefined), [newDatasetKey])
  const createColumns = datasetForNew?.columns ?? []
  const datasetOptions = useMemo(() => getLocalizedDatasets(t).map((dataset) => ({ value: dataset.key, label: dataset.label })), [t])
  const datasetOptionGroups = useMemo(() => REPORT_DATASET_GROUPS.map((group) => ({
    label: t(`reports.columnGroup.${group.key}`),
    options: datasetOptions.filter((option) => group.datasets.includes(option.value))
  })), [datasetOptions, t])

  // v2.2 findings.md #188: a stock-based column of a "Stock reconciliado"
  // report says so in its header — only when a count was really spread
  // (findings #222), otherwise the figures are the recorded ones.
  const markReconciled = useCallback(
    (columns: typeof createColumns, scope: ReportScopeV1 | null | undefined, differences: StockDifferenceSummary | null | undefined) => (
      scope?.stockView === 'reconciled' && (differences?.counts ?? 0) > 0
        ? columns.map((column) => (column.stockBased
          ? { ...column, label: `${column.label} ${t('reports.stockView.columnSuffix')}` }
          : column))
        : columns
    ),
    [t]
  )

  const draftColumns = useMemo(
    () => markReconciled(orderedReportColumns(createColumns, newColumns), newScope, draftDifferences),
    [createColumns, newColumns, newScope, markReconciled, draftDifferences]
  )
  const draftHasStockColumns = createColumns.some((column) => column.stockBased)

  const draftScopedRows = useMemo(
    () => sortRowsNewestFirst(localizeReportRows(t, decorateRowsWithRemovedFarmLotLabel(
      applyReportScope(draftPreview?.rows ?? [], newScope),
      knownFarmIds,
      t('reports.cell.removedFarmSuffix')
    ))),
    [draftPreview, newScope, knownFarmIds, t]
  )
  const draftPreviewRows = draftScopedRows.slice(0, DRAFT_PREVIEW_ROWS)

  const draftReconciled = newScope.stockView === 'reconciled'
  useEffect(() => {
    let cancelled = false
    if (!draftReconciled || !isCreateFormOpen) {
      setDraftDifferences(null)
      return
    }
    void summarizeScopeDifferences(newScope).then((summary) => {
      if (!cancelled) setDraftDifferences(summary)
    })
    return () => {
      cancelled = true
    }
  }, [draftReconciled, isCreateFormOpen, newScope])

  // Preview-before-save: runs the in-progress draft (not yet persisted)
  // against real data so a misconfigured scope/columns/FX shows up before
  // Save, instead of only being discoverable afterwards from the Saved
  // reports list.
  const handlePreviewDraft = async () => {
    if (!datasetForNew || !newDatasetKey) {
      setDraftPreview({ loading: false, error: t('reports.error.unknownDataset', { datasetKey: newDatasetKey }), rows: [] })
      return
    }

    setDraftPreview({ loading: true, error: null, rows: [] })
    try {
      const draftReport = createReport({
        id: editingReportId || undefined,
        createdAt: editingReportId ? editingCreatedAt : undefined,
        name: newName,
        datasetKey: newDatasetKey,
        visibility: newVisibility,
        columns: newColumns,
        scope: newScope,
        fx: newDatasetKey === 'lotCostPerKg' ? normalizeFxDraft(newFx) : null
      })
      await prepareDeletedLotFilter()
      const rows = await datasetForNew.fetchRows(newScope, draftReport)
      setDraftPreview({ loading: false, error: null, rows, deletedLotRecords: deletedLotRecordsLeftOut() })
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      setDraftPreview({ loading: false, error: message, rows: [] })
    }
  }

  const canCreate = newName.trim().length > 0 && newDatasetKey !== '' && orderedReportColumns(createColumns, newColumns).length > 0 && fxDraftValid

  const reportColumns = useMemo(() => {
    if (!activeReport || !activeDataset) return []
    return markReconciled(
      orderedReportColumns(activeDataset.columns, activeReport.columns),
      activeReport.scope,
      runState.reportId === activeReport.id ? runState.stockDifferences : null
    )
  }, [activeDataset, activeReport, markReconciled, runState.reportId, runState.stockDifferences])

  const farmById = useMemo(() => new Map(farms.map((farm) => [farm.id, farm] as const)), [farms])
  const productionUnitOptions = useMemo(() => {
    const units: Array<{ key: string; value: string; label: string }> = []
    farms.forEach((farm) => {
      const unitsForFarm = (farm.productionUnits ?? []) as WizardProductionUnit[]
      unitsForFarm.forEach((unit) => {
        units.push({
          key: unit.id,
          value: unit.id,
          label: `${farm.name} · ${unit.name || unit.id}`
        })
      })
    })
    return units.sort((a, b) => a.label.localeCompare(b.label))
  }, [farms])

  const farmOptions = useMemo(
    () => farms.map((farm) => ({ key: farm.id, value: farm.id, label: farm.name || farm.id })).sort((a, b) => a.label.localeCompare(b.label)),
    [farms]
  )

  const productionUnitLabelById = useMemo(() => {
    const map = new Map<string, string>()
    farms.forEach((farm) => {
      ;(farm.productionUnits ?? []).forEach((unit) => {
        map.set(unit.id, unit.name || unit.id)
      })
    })
    return map
  }, [farms])
  const lotKeyById = useMemo(() => new Map(lots.map((lot) => [lot.id, lot.lotKey] as const)), [lots])
  const lotOptions = useMemo(() => {
    const options = lots.map((lot) => {
      const farmLabel = farmById.get(lot.farmId)?.name ?? lot.farmId
      const unitLabel = lot.productionUnitId
        ? (productionUnitLabelById.get(lot.productionUnitId) ?? lot.productionUnitId)
        : '—'
      return {
        key: lot.id,
        value: lot.id,
        label: `${farmLabel} · ${unitLabel} · ${lot.lotKey}`
      }
    })
    return options.sort((a, b) => a.label.localeCompare(b.label))
  }, [lots, farmById, productionUnitLabelById])

  const displayedRows = useMemo(() => {
    if (!activeReport) return []
    if (runState.reportId !== activeReport.id) return []
    return runState.rows
  }, [activeReport, runState.reportId, runState.rows])

  const scopedRows = useMemo(() => {
    return localizeReportRows(t, decorateRowsWithRemovedFarmLotLabel(
      applyReportScope(displayedRows, activeReport?.scope ?? null),
      knownFarmIds,
      t('reports.cell.removedFarmSuffix')
    ))
  }, [displayedRows, activeReport?.scope, knownFarmIds, t])

  // A sort column from the previous report may not exist on this one (or
  // mean something different) — start fresh whenever the active report
  // changes.
  useEffect(() => {
    setSortState(null)
  }, [activeReportId])

  // Findings #223: back to the first page whenever the rows change order.
  useEffect(() => {
    setPage(0)
  }, [activeReportId, sortState, pageSize, runState.rows])

  const sortedRows = useMemo(() => {
    if (!sortState) return sortRowsNewestFirst(scopedRows)
    const { columnKey, direction } = sortState
    const factor = direction === 'asc' ? 1 : -1
    return [...scopedRows].sort((a, b) => {
      const aValue = a[columnKey]
      const bValue = b[columnKey]
      if (isNumericLikeValue(aValue) && isNumericLikeValue(bValue)) {
        return (Number(aValue) - Number(bValue)) * factor
      }
      const aText = stringifyCell(aValue, '')
      const bText = stringifyCell(bValue, '')
      return aText.localeCompare(bText) * factor
    })
  }, [scopedRows, sortState])

  const pagedRows = useMemo(() => sortedRows.slice(page * pageSize, (page + 1) * pageSize), [sortedRows, page, pageSize])

  const handleSortColumn = (columnKey: string) => {
    setSortState((prev) => {
      if (!prev || prev.columnKey !== columnKey) return { columnKey, direction: 'asc' }
      if (prev.direction === 'asc') return { columnKey, direction: 'desc' }
      return null
    })
  }

  const handlePublishInternally = () => {
    if (!activeReport || !activeDataset || reportColumns.length === 0 || scopedRows.length === 0) {
      setPublishError(t('reports.publish.error.noPreview'))
      setPublishStatus(null)
      return
    }

    try {
      const detail = publishReportSnapshotArtifact({
        report: activeReport,
        datasetLabel: activeDataset.label,
        columns: reportColumns.map((column) => ({ key: column.key, label: column.label })),
        rows: sortedRows
      })

      setPublishedArtifactVersion({
        version: detail.overview.currentVersion,
        publishedAt: detail.overview.publishedAt
      })
      setPublishError(null)
      setPublishStatus(t('reports.publish.success', { version: String(detail.overview.currentVersion) }))
      navigate(`/community/artifacts/${encodeURIComponent(detail.artifactId)}`)
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      setPublishStatus(null)
      setPublishError(t('reports.publish.error.failed', { error: message }))
    }
  }

  const handleExportCsv = () => {
    if (!activeReport || reportColumns.length === 0 || scopedRows.length === 0) return
    const csv = buildCsvContent(reportColumns, sortedRows, t('reports.cell.unserializable'))
    downloadTextBlob({
      text: csv,
      filename: `${sanitizeFileName(activeReport.name)}.csv`,
      mimeType: 'text/csv;charset=utf-8;'
    })
  }

  const handleSaveReport = () => {
    // v2.2 T3-B-08: when editingReportId is set, reuse the stored id +
    // preserve createdAt so `upsertReport` overwrites the existing entry
    // instead of minting a new report. Otherwise, fall through to the
    // create path (unchanged from pre-T3-B-08).
    if (!newDatasetKey) return
    const editing = editingReportId.trim().length > 0
    const report = createReport({
      id: editing ? editingReportId : undefined,
      createdAt: editing ? editingCreatedAt : undefined,
      name: newName,
      datasetKey: newDatasetKey,
      visibility: newVisibility,
      columns: newColumns,
      scope: newScope,
      fx: newDatasetKey === 'lotCostPerKg' ? normalizeFxDraft(newFx) : null
    })
    const next = upsertReport(report)
    setReports(next)
    setActiveReportId(report.id)
    setNewName('')
    setNewScope(emptyScope())
    setNewFx(null)
    setDraftPreview(null)
    setIsCreateFormOpen(false)
    if (editing) {
      setEditingReportId('')
      setEditingCreatedAt('')
      setEditingOriginalName('')
    }
  }

  return (
    // Shape-up: wider than the app's default 1120px cap, matching Panel —
    // the scope section's 3-up choice lists and the preview table both
    // benefit from the extra room, same reasoning as Panel's widget grid.
    <PageShell style={{ maxWidth: '1800px' }}>
      <h2 className="h2">{t('reports.title')}</h2>
      <p className="lead">{t('reports.subtitle')}</p>

      {/* Shape-up: the create/edit form is a drawer trigger, not permanent
          on-screen real estate, mirroring Panel's Add widget trigger. */}
      <section
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
          padding: 14,
          borderRadius: 12,
          border: '1px solid #e5e7eb',
          backgroundColor: '#ffffff',
          marginTop: 16
        }}
      >
        <div style={{ display: 'grid', gap: 4, minWidth: 0 }}>
          <strong style={{ fontSize: 14 }}>{t('reports.create.title')}</strong>
          <span style={{ fontSize: 12, color: '#4b5563' }}>{t('reports.create.trigger.subtitle')}</span>
        </div>
        <button type="button" className="btn-gradient" onClick={handleOpenCreateNew}>
          {t('reports.button.newReport')}
        </button>
      </section>

      <Drawer
        open={isCreateFormOpen}
        onClose={handleCloseCreateForm}
        title={editingReportId.trim().length > 0 ? t('reports.edit.title') : t('reports.create.title')}
      >
        <div className="form-grid">
          <FormField
            id="report-name"
            label={t('reports.field.name')}
            value={newName}
            placeholder={t('reports.field.name.placeholder')}
            onChange={(value) => setNewName(String(value))}
          />

          <FormField
            id="report-dataset"
            label={t('reports.field.dataset')}
            type="select"
            value={newDatasetKey}
            placeholder={t('reports.field.dataset.placeholder')}
            options={datasetOptions}
            optionGroups={datasetOptionGroups}
            onChange={(value) => {
              const nextKey = String(value) as ReportDatasetKey
              setNewDatasetKey(nextKey)

              const dataset = getLocalizedDataset(t, nextKey)
              setNewColumns(dataset ? defaultReportColumns(dataset.columns) : [])
              if (nextKey !== 'lotCostPerKg') {
                setNewFx(null)
              }
            }}
          />

          <FormField
            id="report-visibility"
            label={t('reports.field.visibility')}
            type="select"
            value={newVisibility}
            options={[
              { value: 'private', label: t('reports.visibility.private') },
              { value: 'team', label: t('reports.visibility.team') }
            ]}
            onChange={(value) => {
              const next = String(value)
              setNewVisibility(isVisibility(next) ? next : 'private')
            }}
          />
        </div>

        <div style={{ marginTop: 12 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>{t('reports.columns.title')}</div>
          {createColumns.length === 0 ? (
            <div className="lead">{t('reports.columns.noneAvailable')}</div>
          ) : (
            <ReportColumnPicker columns={createColumns} selected={newColumns} onChange={setNewColumns} />
          )}
        </div>

        <div style={{ marginTop: 16, display: 'grid', gap: 12 }}>
          <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 12 }}>
            <div style={{ fontWeight: 700 }}>{t('reports.fx.sourceTitle')}</div>
            <div className="lead" style={{ marginTop: 6 }}>{t('reports.fx.sourceBody')}</div>
          </div>

          <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 12 }}>
            <div style={{ fontWeight: 700 }}>{t('reports.fx.convertedTitle')}</div>
            <div className="lead" style={{ marginTop: 6 }}>{t('reports.fx.subtitle')}</div>

            {!isNewDatasetFxCapable ? (
              <div className="lead" style={{ marginTop: 8 }}>{t('reports.fx.notAvailable')}</div>
            ) : (
              <div style={{ display: 'grid', gap: 12, marginTop: 10 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={fxDraftEnabled}
                    onChange={(event) => {
                      if (event.target.checked) {
                        setNewFx((prev) => prev ?? emptyFx())
                        return
                      }
                      setNewFx(null)
                    }}
                  />
                  <span>{t('reports.fx.enable')}</span>
                </label>

                {fxDraftEnabled ? (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                    <label style={{ display: 'grid', gap: 6 }}>
                      <span style={{ fontWeight: 700 }}>{t('reports.fx.targetCurrency')}</span>
                      <input
                        value={newFx?.targetCurrency ?? ''}
                        onChange={(event) => {
                          const nextValue = event.target.value.toUpperCase()
                          setNewFx((prev) => (prev ? { ...prev, targetCurrency: nextValue } : prev))
                        }}
                        placeholder="COP"
                      />
                    </label>

                    <label style={{ display: 'grid', gap: 6 }}>
                      <span style={{ fontWeight: 700 }}>{t('reports.fx.asOf')}</span>
                      <DateTimeLocalInput
                        value={newFx?.asOf ?? ''}
                        onChangeValue={(value) => {
                          setNewFx((prev) => (prev ? { ...prev, asOf: value } : prev))
                        }}
                      />
                    </label>
                  </div>
                ) : (
                  <div className="lead">{t('reports.fx.off')}</div>
                )}

                <div className="lead">{t('reports.fx.convertedMoney')}</div>
                <div className="lead">{t('reports.fx.diagnostics')}</div>
              </div>
            )}
          </div>
        </div>

        <div style={{ marginTop: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 8, cursor: 'help' }} title={t('reports.help.filterHint')}>
            {t('reports.scope.title')} <span aria-hidden="true" style={{ fontSize: 12, color: '#64748b' }}>ⓘ</span>
          </div>
          <div className="lead" style={{ marginBottom: 10 }}>
            {t('reports.scope.subtitle')}
          </div>

          <div style={{ display: 'grid', gap: 14 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
              <label style={{ display: 'grid', gap: 6 }}>
                <span style={{ fontWeight: 700 }}>{t('reports.scope.from')}</span>
                <DateInput
                  value={newScope.fromDate}
                  onChangeValue={(value) => setNewScope((prev) => ({ ...prev, fromDate: value }))}
                />
              </label>
              <label style={{ display: 'grid', gap: 6 }}>
                <span style={{ fontWeight: 700 }}>{t('reports.scope.to')}</span>
                {/* v2.2 T3-B-06f: rolling `toDate = 'today'` sentinel. When
                    `Today` is chosen, the persisted `toDate` is ignored
                    and the dataset resolver computes today in the farm's
                    local timezone at fetch time. Disable the DateInput
                    so it's clear the value won't be used. */}
                <select
                  aria-label={t('reports.scope.toDateMode.aria')}
                  value={newScope.toDateMode ?? 'fixed'}
                  onChange={(event) => {
                    const nextMode = event.target.value === 'today' ? 'today' : 'fixed'
                    setNewScope((prev) => ({ ...prev, toDateMode: nextMode }))
                  }}
                >
                  <option value="fixed">{t('reports.scope.toDateMode.fixed')}</option>
                  <option value="today">{t('reports.scope.toDateMode.today')}</option>
                </select>
                <DateInput
                  value={newScope.toDate}
                  disabled={newScope.toDateMode === 'today'}
                  onChangeValue={(value) => setNewScope((prev) => ({ ...prev, toDate: value }))}
                />
                {newScope.toDateMode === 'today' && (
                  <span className="lead" style={{ fontSize: 12 }}>
                    {t('reports.scope.toDateMode.todayHint')}
                  </span>
                )}
              </label>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14 }}>
              <ChoiceList
                title={t('reports.scope.facilities')}
                clearLabel={clearLabel}
                emptyLabel={emptyChoiceLabel}
                choices={farmOptions}
                selectedValues={newScope.farmIds}
                onChange={(updater) =>
                  setNewScope((prev) => ({
                    ...prev,
                    farmIds: typeof updater === 'function' ? updater(prev.farmIds) : updater
                  }))
                }
              />

              <ChoiceList
                title={t('reports.scope.productionUnits')}
                clearLabel={clearLabel}
                emptyLabel={emptyChoiceLabel}
                choices={productionUnitOptions}
                selectedValues={newScope.productionUnitIds}
                onChange={(updater) =>
                  setNewScope((prev) => ({
                    ...prev,
                    productionUnitIds:
                      typeof updater === 'function' ? updater(prev.productionUnitIds) : updater
                  }))
                }
              />

              <ChoiceList
                title={t('reports.scope.lots')}
                clearLabel={clearLabel}
                emptyLabel={emptyChoiceLabel}
                choices={lotOptions}
                selectedValues={newScope.lotIds}
                onChange={(updater) =>
                  setNewScope((prev) => ({
                    ...prev,
                    lotIds: typeof updater === 'function' ? updater(prev.lotIds) : updater,
                    lotKeys: Array.from(
                      new Set(
                        (typeof updater === 'function' ? updater(prev.lotIds) : updater)
                          .map((lotId) => lotKeyById.get(lotId) ?? '')
                          .filter(Boolean)
                      )
                    )
                  }))
                }
              />
            </div>

            {draftHasStockColumns && (
              <fieldset style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 12, margin: 0 }}>
                <legend style={{ fontWeight: 700, padding: '0 4px' }}>{t('reports.stockView.title')}</legend>
                <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    type="radio"
                    name="report-stock-view"
                    checked={newScope.stockView !== 'reconciled'}
                    onChange={() => setNewScope((prev) => ({ ...prev, stockView: 'recorded' }))}
                  />
                  {t('reports.stockView.recorded')}
                </label>
                <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
                  <input
                    type="radio"
                    name="report-stock-view"
                    checked={newScope.stockView === 'reconciled'}
                    onChange={() => setNewScope((prev) => ({ ...prev, stockView: 'reconciled' }))}
                  />
                  {t('reports.stockView.reconciled')}
                </label>
                <StockViewExplainer reconciled={draftReconciled} differences={draftDifferences} />
              </fieldset>
            )}
          </div>
        </div>

        <div style={{ marginTop: 16 }}>
          <button type="button" onClick={() => { void handlePreviewDraft() }} disabled={!datasetForNew || draftColumns.length === 0}>
            {t('reports.button.previewDraft')}
          </button>

          {draftPreview?.loading && <p className="lead">{t('reports.preview.loading')}</p>}
          {draftPreview?.error && <p className="lead">{t('reports.preview.runFailed', { error: draftPreview.error })}</p>}

          {draftPreview && !draftPreview.loading && !draftPreview.error && (
            draftScopedRows.length === 0 || draftColumns.length === 0 ? (
              <p className="lead">{t('reports.preview.clickRun')}</p>
            ) : (
              <div>
                <p className="lead" style={{ marginTop: 8 }}>
                  {t('reports.preview.scopeApplied', { total: draftPreview.rows.length, scoped: draftScopedRows.length })}
                  {draftScopedRows.length > draftPreviewRows.length
                    ? ` · ${t('reports.pager.showing', { from: 1, to: draftPreviewRows.length, total: draftScopedRows.length })}`
                    : ''}
                </p>
                <DeletedLotRecordsNote count={draftPreview.deletedLotRecords ?? 0} />
                <div style={tableWrapperStyle}>
                  <table style={tableStyle}>
                    <thead>
                      <tr>
                        {draftColumns.map((column) => (
                          <th key={column.key} style={tableHeaderCellStyle}>
                            <span aria-hidden="true" style={{ marginRight: 4, color: '#94a3b8', textTransform: 'none' }}>{COLUMN_KIND_ICON[columnKind(column)]}</span>
                            {column.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {draftPreviewRows.map((row, rowIndex) => (
                        <tr key={typeof row.id === 'string' ? row.id : rowIndex} style={tableRowStyle(rowIndex)}>
                          {draftColumns.map((column) => {
                            const value = row[column.key]
                            return (
                              <td
                                key={column.key}
                                style={{
                                  ...tableCellStyle(rowIndex === draftPreviewRows.length - 1),
                                  textAlign: cellTextAlign(column.key, value)
                                }}
                              >
                                {stringifyCell(value, t('reports.cell.unserializable'))}
                              </td>
                            )
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )
          )}
        </div>

        <div style={{ marginTop: 16, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            type="button"
            className="btn-gradient"
            disabled={!canCreate}
            onClick={handleSaveReport}
          >
            {t('reports.button.save')}
          </button>
          <button type="button" onClick={handleCloseCreateForm}>
            {t('common.cancel')}
          </button>
          {editingReportId.trim().length > 0 && (
            <span className="lead" aria-live="polite">
              {t('reports.form.editingHint', { name: editingOriginalName })}
            </span>
          )}
        </div>
      </Drawer>

      <div className="form-card" style={{ marginTop: 16 }}>
        <h3 className="h2" style={{ marginTop: 0 }}>{t('reports.saved.title')}</h3>

        {reports.length === 0 ? (
          <p className="lead">{t('reports.saved.empty')}</p>
        ) : (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 10 }}>
            {reports.map((report) => (
              <li
                key={report.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  flexWrap: 'wrap'
                }}
              >
                <div>
                  <div style={{ fontWeight: 700 }}>
                    {report.name}
                  </div>
                  <div className="lead">
                    {getLocalizedDataset(t, report.datasetKey)?.label ?? report.datasetKey} · {report.visibility === 'team' ? t('reports.visibility.team') : t('reports.visibility.private')}
                  </div>
                  {report.datasetKey === 'lotCostPerKg' && (
                    <div className="lead" style={{ marginTop: 4 }}>
                      {report.fx
                        ? t('reports.fx.summaryOn', {
                            currency: report.fx.targetCurrency,
                            asOf: new Date(report.fx.asOf).toLocaleString()
                          })
                        : t('reports.fx.summaryOff')}
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className="btn-gradient"
                    onClick={() => {
                      void handleRun(report)
                    }}
                  >
                    {t('reports.button.run')}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleStartEdit(report)}
                  >
                    {t('reports.button.edit')}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDuplicate(report)}
                  >
                    {t('reports.button.duplicate')}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSendToPanel(report)}
                  >
                    {t('reports.button.sendToPanel')}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(report.id)}
                  >
                    {t('common.delete')}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="form-card" style={{ marginTop: 16 }}>
        <h3 className="h2" style={{ marginTop: 0 }}>{t('reports.results.title')}</h3>

        {activeReport ? (
          <>
            <div className="lead">
              {activeReport.name} · {activeDataset?.label ?? activeReport.datasetKey}
            </div>

            {activeReport.datasetKey === 'lotCostPerKg' && (
              <div className="lead" style={{ marginTop: 8 }}>
                {activeReport.fx
                  ? t('reports.fx.summaryOn', {
                      currency: activeReport.fx.targetCurrency,
                      asOf: new Date(activeReport.fx.asOf).toLocaleString()
                    })
                  : t('reports.fx.summaryOff')}
              </div>
            )}

            {activeReport.datasetKey === 'lotCostPerKg' && <FacilityCostsSummary scope={activeReport.scope} />}

            {activeReport.datasetKey === 'lotCostPerKg' && (
              <div style={{ marginTop: 12, display: 'grid', gap: 8, border: '1px solid #e5e7eb', borderRadius: 12, padding: 12 }}>
                <div style={{ fontWeight: 700 }}>{t('reports.fx.previewTitle')}</div>
                <div className="lead">{t('reports.fx.sourceBody')}</div>
                {activeReport.fx ? (
                  <div className="lead">{t('reports.fx.convertedMoney')}</div>
                ) : (
                  <div className="lead">{t('reports.fx.off')}</div>
                )}
                <div className="lead">{t('reports.fx.diagnostics')}</div>
              </div>
            )}

            {activeReport.scope && (
              <div className="lead" style={{ marginTop: 10 }}>
                {t('reports.preview.scopeApplied', { total: displayedRows.length, scoped: scopedRows.length })}
              </div>
            )}

            <div style={{ marginTop: 12, display: 'grid', gap: 8 }}>
              <div className="lead">{t('reports.publish.subtitle')}</div>

              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                <button
                  type="button"
                  className="btn-gradient"
                  disabled={runState.loading || scopedRows.length === 0 || reportColumns.length === 0}
                  onClick={handlePublishInternally}
                >
                  {t('reports.button.publishInternal')}
                </button>

                <button
                  type="button"
                  disabled={scopedRows.length === 0 || reportColumns.length === 0}
                  onClick={handleExportCsv}
                >
                  {t('reports.button.exportCsv')}
                </button>

                {publishedArtifactVersion ? (
                  <div className="lead" data-testid="reports-publish-current">
                    {t('reports.publish.current', { version: String(publishedArtifactVersion.version) })}
                    {publishedArtifactVersion.publishedAt
                      ? ` · ${t('reports.publish.publishedAt', { date: new Date(publishedArtifactVersion.publishedAt).toLocaleString() })}`
                      : ''}
                  </div>
                ) : (
                  <div className="lead" data-testid="reports-publish-none">
                    {t('reports.publish.noneYet')}
                  </div>
                )}
              </div>

              {publishStatus ? (
                <div className="lead" data-testid="reports-publish-status">{publishStatus}</div>
              ) : null}

              {publishError ? (
                <div className="lead" data-testid="reports-publish-error">{publishError}</div>
              ) : null}
            </div>

            {runState.loading && runState.reportId === activeReport.id && (
              <p className="lead">{t('reports.preview.loading')}</p>
            )}

            {runState.error && runState.reportId === activeReport.id && (
              <p className="lead">{t('reports.preview.runFailed', { error: runState.error })}</p>
            )}

            {!runState.loading && runState.reportId === activeReport.id && (
              <DeletedLotRecordsNote count={runState.deletedLotRecords ?? 0} />
            )}

            {!runState.loading && runState.reportId === activeReport.id && activeReport.scope?.stockView === 'reconciled' && (
              <StockViewSummary differences={runState.stockDifferences ?? null} />
            )}

            {!runState.loading && displayedRows.length === 0 && (
              <p className="lead">{t('reports.preview.clickRun')}</p>
            )}

            {scopedRows.length > 0 && reportColumns.length > 0 && (
              <div style={scrollableTableWrapperStyle}>
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      {reportColumns.map((column) => {
                        const isSorted = sortState?.columnKey === column.key
                        return (
                          <th
                            key={column.key}
                            style={sortableTableHeaderCellStyle}
                            onClick={() => handleSortColumn(column.key)}
                            aria-sort={isSorted ? (sortState!.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                            title={column.help}
                          >
                            <span aria-hidden="true" style={{ marginRight: 4, color: '#94a3b8', textTransform: 'none' }}>{COLUMN_KIND_ICON[columnKind(column)]}</span>
                            {column.label}
                            <span style={{ marginLeft: 4, color: isSorted ? '#334155' : '#cbd5e1' }}>
                              {isSorted ? (sortState!.direction === 'asc' ? '▲' : '▼') : '⇅'}
                            </span>
                            {column.derived && (
                              <span
                                title={t('reports.cell.derivedTooltip')}
                                style={{ marginLeft: 4, color: '#94a3b8', fontWeight: 400, cursor: 'help', textTransform: 'none' }}
                              >
                                ~
                              </span>
                            )}
                          </th>
                        )
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {pagedRows.map((row, rowIndex) => (
                      <tr key={typeof row.id === 'string' ? row.id : rowIndex} style={tableRowStyle(rowIndex)}>
                        {reportColumns.map((column) => {
                          const value = row[column.key]
                          return (
                            <td
                              key={column.key}
                              style={{
                                ...tableCellStyle(rowIndex === pagedRows.length - 1),
                                textAlign: cellTextAlign(column.key, value),
                                fontStyle: column.derived ? 'italic' : 'normal',
                                color: column.derived ? '#64748b' : 'inherit'
                              }}
                            >
                              {stringifyCell(value, t('reports.cell.unserializable'))}
                            </td>
                          )
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {scopedRows.length > 0 && reportColumns.length > 0 && (
              <ReportPager total={sortedRows.length} page={page} pageSize={pageSize} onPage={setPage} onPageSize={setPageSize} />
            )}
          </>
        ) : (
          <p className="lead">{t('reports.preview.selectPrompt')}</p>
        )}
      </div>
    </PageShell>
  )
}
